import type { QRCodeToStringOptions } from 'qrcode';

import { timezoneAbbreviation } from '../event-discovery/constants';

/**
 * The ticket's copy and every decision in it (`MEM-022` AC-1, AC-2).
 *
 * There is no DOM renderer in this repository and `apps/web`'s test script matches `.test.ts`
 * only, so a rule that lives inside a component body is a rule with no test. Everything on this
 * screen that could be wrong — which token goes in the QR, which timezone the start time is
 * rendered in, what the admission line says — is therefore a function over data here, and
 * `ticket-page.tsx` is left with layout.
 */

/**
 * The screen's title.
 *
 * "Your ticket", not "Ticket": a member reaches this from their own registration, and the
 * possessive is what distinguishes it from the door's screen, which shows somebody else's.
 */
export const TICKET_HEADING = 'Your ticket';

/** The prototype's band over the ticket, in its own words (`Web App.dc.html` line 1140). */
export const TICKET_BAND_LABEL = 'H2HSpark ticket';

/** The line under the code, from the same overlay: what the member does with it. */
export const SCAN_AT_CHECK_IN = 'Show this at check-in';

/** The control that opens the ticket from the event page's header card (`MEM-053` AC-1). */
export const VIEW_TICKET_LABEL = 'View your ticket';

/** The dialog's own control, the prototype's full-width outline button. */
export const CLOSE_TICKET_LABEL = 'Close';

/** What the QR is for, said once, above it. */
export const SCAN_PROMPT = 'Show this at the door.';

export const BACK_TO_EVENT_LABEL = 'Back to event';

/** The `alt` on the QR image. It describes the image's *purpose*, because its content — a
 * 43-character token — is not something a screen-reader user can act on, and reading it aloud in
 * a queue would hand the code to everyone within earshot (`NFR accessibility#wcag-aa`). */
export const QR_ALTERNATIVE_TEXT = 'A QR code for the door to scan.';

/**
 * `MEM-022` AC-1's last line, verbatim: **"Admits 1 · order #{order number}"**.
 *
 * The order number is the registration's `confirmationCode` — a minted display code
 * (`K7M2P-QR4WX`), never the registration's or the event's identifier. That distinction is the
 * whole point of the field existing: `MEM-017` AC-1 already prints it on the confirmed panel with
 * the same `#` prefix, so a member reading their ticket and reading their confirmation sees one
 * number rather than two.
 *
 * "Admits 1" is fixed rather than counted. Every registration in this build is one person —
 * party size, guests and multi-seat orders arrive with paid ticketing (`MEM-020`, `MEM-021`) — so
 * a computed count here would be a variable with one possible value, which is a worse lie than a
 * constant when the second value finally shows up.
 */
export function ticketLine(confirmationCode: string): string {
  return `Admits 1 · order #${confirmationCode}`;
}

/**
 * `MEM-022` AC-1's "the date and start time", rendered **in the event's own timezone**.
 *
 * ## The zone is a parameter with no default, deliberately
 *
 * `docs/standards/time-and-timezone.md#display` is explicit: format with `Intl.DateTimeFormat`
 * and `timeZone` set, never the browser default, and *"a helper that defaults the zone parameter
 * will silently give you `America/New_York` for a Chicago event"*. So this function reads the
 * event's stored zone and nothing else — the device's zone is never consulted, and there is no
 * fallback to it.
 *
 * An event with no stored zone renders in **UTC and says UTC**, which is `MEM-015` AC-2's
 * decision applied here for the same reason: a visible `UTC` is a member asking the organizer,
 * while a silent local time is a member arriving at the wrong hour.
 *
 * ## Why the abbreviation is always shown here
 *
 * The discovery card hides it when it matches the reader's own zone, which keeps the common case
 * clean. **A ticket does not get that luxury.** It is read at home the night before and again in
 * the queue, possibly in a different zone from either, and it is the artefact the member acts on
 * with an hour at stake. One token — `EDT` — removes the ambiguity permanently, and it also makes
 * this function a pure function of the event alone, which is what lets a test prove the device's
 * zone is not involved.
 *
 * Returns `null` for an event with no start instant, or one that cannot be parsed. A ticket with
 * no date is not a degraded ticket; it is a wrong one, and the screen says the date is missing
 * rather than printing "Invalid Date".
 */
export function formatTicketDateTime(event: {
  readonly startsAt: string | null;
  readonly timezone: string | null;
}): string | null {
  if (event.startsAt === null) return null;
  const start = new Date(event.startsAt);
  if (Number.isNaN(start.getTime())) return null;

  const zone = event.timezone ?? 'UTC';
  let date: string;
  let time: string;
  try {
    date = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    }).format(start);
    time = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      hour: 'numeric',
      minute: '2-digit',
    }).format(start);
  } catch {
    // An IANA name this platform's ICU cannot resolve. Falling back to the device's zone would
    // print a confident wrong hour, which is the failure this whole helper exists to prevent.
    return null;
  }

  const abbreviation = timezoneAbbreviation(start, zone);
  return abbreviation === null ? `${date} · ${time}` : `${date} · ${time} ${abbreviation}`;
}

/**
 * `MEM-022` AC-1's "the venue name and city", as one line: `The Wexford Room · Providence`.
 *
 * Either half may be absent and the line still has to be true, so nothing is invented and no
 * placeholder dash stands in for a missing half. A ticket with neither renders no venue line at
 * all — the member has the event's own page for that, and a line reading "· " is worse than
 * silence.
 *
 * The street address is **not** here on purpose. `MEM-016` lets an event withhold it, and the
 * ticket is the one screen that gets photographed and forwarded; the city is what a member needs
 * to know they are at the right venue, and the API decides whether the address is theirs to see.
 */
export function venueLine(venueName: string | null, city: string | null): string | null {
  const parts = [venueName, city].filter(
    (part): part is string => part !== null && part.trim().length > 0,
  );
  return parts.length === 0 ? null : parts.join(' · ');
}

/**
 * `MEM-022` AC-1's "the registrant's name", as the account holds it.
 *
 * `null` — and then the screen prints no name line — for an account with no display name. The
 * two obvious substitutes are both worse: the email address turns a screen held up in public into
 * a contact-details leak, and a user id would put a UUID on a ticket that is supposed to name a
 * person. A door with a name-less ticket falls back to the scan, which is the code path that
 * actually admits somebody anyway.
 */
export function registrantName(displayName: string | null): string | null {
  const trimmed = displayName?.trim() ?? '';
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * The shape of a token the API mints: 43 base64url characters, which is 32 random bytes encoded.
 *
 * Checked on the client not as a security control — it is not one, the server owns that — but as
 * a guard against rendering an unscannable square. See `qrPayload`.
 */
export const MEMBER_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;

/**
 * `MEM-022` AC-2: **the QR carries the ticket token.**
 *
 * ## The signature is the enforcement
 *
 * This takes `{ ticketToken }` and not the whole `MemberTicketTokens` response, so the connect
 * token is not in scope at the one point in the app that decides what gets encoded. That matters
 * because the two are indistinguishable to look at — both are 43 base64url characters — and the
 * door's scanner resolves either one and routes by type (`EVT-029`). A ticket that quietly
 * encoded the connect token would scan, and would then do the wrong thing rather than fail; the
 * type system is a cheaper guard against that than a code review.
 *
 * Nothing derived from the order number goes near this. `SYS-014` requires the token to be
 * non-guessable and `MEM-022`'s definition of done asks for a test that it is not derivable from
 * the order number — the token is minted from random bytes server-side and simply carried here.
 *
 * Returns `null` when the value is not token-shaped, so the screen can say the ticket is not
 * ready instead of printing a QR that scans to nonsense at the door.
 */
export function qrPayload(tokens: { readonly ticketToken: string }): string | null {
  return MEMBER_TOKEN_PATTERN.test(tokens.ticketToken) ? tokens.ticketToken : null;
}

/**
 * How the token is drawn.
 *
 * **SVG, not a raster.** The QR is the one thing on this screen that has to survive a phone at
 * arm's length under a venue's lighting, and a vector scales to whatever the layout gives it
 * without the softening that costs a scanner a read.
 *
 * `errorCorrectionLevel: 'M'` recovers roughly 15% of a damaged symbol, which is the level meant
 * for a screen — 'L' is thinner cover for a fingerprint or a glare band, and 'H' buys robustness
 * this reader does not need by making the modules smaller, which is the opposite of what helps.
 *
 * `margin: 2` is two modules of quiet zone. The specification asks for four; two is the widely
 * used screen compromise, and the panel behind the code is a plain light surface that extends the
 * quiet zone visually well past that.
 *
 * No `color` is set, so the renderer's black-on-white default stands. That is deliberate:
 * contrast here is a scanning requirement rather than a design choice, and a branded purple QR is
 * the single most common way to make a code that photographs beautifully and reads badly. It also
 * keeps a raw hex out of this file, which the design-token rule would otherwise flag.
 */
export const TICKET_QR_OPTIONS: QRCodeToStringOptions = {
  type: 'svg',
  errorCorrectionLevel: 'M',
  margin: 2,
};

/**
 * An SVG the `qrcode` renderer produced, addressed as an image source.
 *
 * `encodeURIComponent` rather than base64: it is exact for the ASCII the renderer emits, it keeps
 * the markup readable in dev tools, and it escapes the `#` that would otherwise truncate the URI
 * at the first colour attribute.
 */
export function qrSvgDataUri(svgMarkup: string): string {
  return `data:image/svg+xml,${encodeURIComponent(svgMarkup)}`;
}

/** Where the ticket lives. One function, so the route, the link on the event page and any deep
 * link cannot disagree. */
export function ticketPath(eventId: string): string {
  return `/app/events/${encodeURIComponent(eventId)}/ticket`;
}

/** What the screen says to a member who has no ticket for this event — no registration, or one
 * they cancelled, which `MEM-017` AC-6 makes the same answer. It is the same answer for an event
 * that does not exist, too: distinguishing them would confirm the event to somebody guessing. */
export const NO_TICKET_TITLE = 'No ticket for this event';
export const NO_TICKET_DESCRIPTION =
  'A ticket appears here once you are registered. If you cancelled, registering again gives you a new one.';

/** The ticket is there but the token is not usable — a malformed payload, or a call that failed.
 * Named separately from `NO_TICKET_*` because it is a different instruction: come back, rather
 * than register. */
export const TICKET_UNAVAILABLE_TITLE = 'Your ticket is not ready';
export const TICKET_UNAVAILABLE_DESCRIPTION =
  'We could not load your code just now. Try again in a moment — and if you are already at the door, staff can find you by name.';

/**
 * `EVT-031` AC-6, written down next to the code that would otherwise grow the control.
 *
 * **A member cannot check themselves in, and this screen says nothing about whether they have
 * been.** Both halves are the refusal. The obvious button — "I'm here" — would make attendance
 * self-asserted, which is exactly what the door exists to prevent; and a status badge is the same
 * mistake one step quieter, because a ticket reading "Not checked in" invites a member to try to
 * fix it, and one reading "Checked in" is a claim this screen cannot make honestly the moment the
 * door's answer changes.
 *
 * So there is no control, no badge, and no arrival time. The ticket presents what `MEM-022` AC-1
 * lists and stops. Check-in is something the organizer does to a scanned token
 * (`EVT-029`, `EVT-030`), and the member's part in it is holding up the code.
 */
export const SELF_CHECK_IN_IS_NOT_BUILT = {
  label: 'Check myself in',
  reason:
    'EVT-031 AC-6 refuses member self-check-in. The ticket carries no check-in control and no ' +
    'check-in status: a control would make attendance self-asserted, and a status badge would ' +
    'invite a member to try to change it. The door scans the code; that is the whole of the ' +
    "member's part in it.",
  stage: 'Refused, not deferred.',
} as const;
